/**
 * New Lead — authenticated lead-intake tool (PHOTON-65).
 *
 * Enters a new lead into the CRM via crm-service `POST /api/leads` (JWT-auth,
 * gated by the `new-lead` tool's `access` permission). Reuses the shared intake
 * engine: dedup + territory auto-assign + opportunity create. "Validate (dry
 * run)" hits /api/leads/dry-run and writes nothing.
 */
import React, { useCallback, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Field,
  Input,
  Textarea,
  Select,
  Switch,
  Button,
  Card,
  Spinner,
} from "@fluentui/react-components";

import useCrmApiClient from "../../hooks/useCrmApiClient";

// --- Allow-lists mirror the server (outbound_intake.py) -----------------------
const RANK_TITLE_OPTIONS = [
  "Chief", "Assistant Chief/ Deputy Chief", "Captain", "Chief Deputy", "Commander",
  "Commissioner", "Corporal", "Deputy", "Detective", "Director/ Marshal", "Lieutenant",
  "Major", "Officer", "Sergeant", "Sheriff", "Undersherrif", "Other",
];
const CAMERA_VENDOR_OPTIONS = [
  "-- None --", "Axon", "Motorola", "Getac", "Wolfcom", "Utility", "Patrol Eyes",
  "Digital Ally", "10-8 Video", "SafeFleet", "Other",
];
const SOURCE_OPTIONS = ["Referral", "Website", "Event / Conference", "Cold Call", "Inbound Call", "Partner", "Other"];
const US_STATES: [string, string][] = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],["CO","Colorado"],
  ["CT","Connecticut"],["DE","Delaware"],["DC","District of Columbia"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],
  ["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],
  ["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],
  ["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],
  ["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const TEXT_FIELDS = [
  "agency_name", "primary_contact_name", "email", "phone", "rank_title", "address1",
  "city", "state", "postal_code", "current_camera_vendor",
  "how_did_you_hear", "timeframe_to_buy", "expected_close_date", "description",
  "officer_count", "car_count", "source",
] as const;
type FieldName = (typeof TEXT_FIELDS)[number];
type FormState = Record<FieldName, string>;

const EMPTY: FormState = TEXT_FIELDS.reduce((acc, k) => ({ ...acc, [k]: "" }), {} as FormState);

const useStyles = makeStyles({
  root: { padding: tokens.spacingVerticalL, maxWidth: "860px", margin: 0, overflowY: "auto", height: "100%" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "start", gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalL },
  full: { gridColumn: "1 / -1" },
  section: { fontSize: tokens.fontSizeBase200, textTransform: "uppercase", letterSpacing: "0.06em", color: tokens.colorNeutralForeground3, margin: `${tokens.spacingVerticalM} 0 ${tokens.spacingVerticalS}` },
  actions: { display: "flex", gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalM },
  result: { marginTop: tokens.spacingVerticalL, padding: tokens.spacingVerticalM },
  ok: { borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}` },
  warn: { borderLeft: `4px solid ${tokens.colorPaletteYellowBorder2}` },
  err: { borderLeft: `4px solid ${tokens.colorPaletteRedBorder2}` },
  toggleRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM },
});

interface ResultState {
  kind: "ok" | "warn" | "err";
  title: string;
  lines: string[];
}

function parseApiError(err: unknown): { message: string; missing?: string[]; allowed?: string[] } {
  const raw = err instanceof Error ? err.message : String(err);
  // useCrmApiClient throws `HTTP <status>: <body>` — body is our JSON detail.
  const m = raw.match(/HTTP \d+:\s*(.*)$/s);
  if (m) {
    try {
      const body = JSON.parse(m[1]);
      const d = body.detail || body;
      return { message: d.message || raw, missing: d.missing_fields, allowed: d.allowed };
    } catch {
      /* not JSON */
    }
  }
  return { message: raw };
}

export const NewLead: React.FC = () => {
  const styles = useStyles();
  const crmApi = useCrmApiClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [qualified, setQualified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);

  const set = useCallback((name: FieldName, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const buildPayload = useCallback(() => {
    const p: Record<string, unknown> = {};
    for (const k of TEXT_FIELDS) {
      const v = (form[k] || "").trim();
      if (v) p[k] = v;
    }
    if (qualified) p.qualified = true;
    return p;
  }, [form, qualified]);

  const clientValidate = useCallback((): string | null => {
    if (!form.agency_name.trim()) return "Agency name is required.";
    if (!form.primary_contact_name.trim()) return "Primary contact name is required.";
    if (!form.email.trim() && !form.phone.trim()) return "Enter at least an email or a phone.";
    if (!form.state.trim()) return "State is required.";
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return "Email address looks invalid.";
    for (const c of ["officer_count", "car_count"] as const) {
      if (form[c].trim() && !/^\d+$/.test(form[c].trim())) return "BWC / in-car counts must be whole numbers.";
    }
    return null;
  }, [form]);

  const submit = useCallback(async () => {
    setResult(null);
    const invalid = clientValidate();
    if (invalid) { setResult({ kind: "err", title: "Check the form", lines: [invalid] }); return; }

    setBusy(true);
    try {
      const data = await crmApi.post<any>("/api/leads", buildPayload());
      const lines = [
        `Opportunity ${data.opportunity_action} → ${data.stage} (${data.opportunity_status})`,
        `Contact ${data.contact_action}`,
        `Assigned to: ${data.assigned_to_name || data.assigned_to || "—"}`,
      ];
      if (data.review_reasons?.length) lines.push(`Flagged for review: ${data.review_reasons.join("; ")}`);
      setResult({ kind: data.review_reasons?.length ? "warn" : "ok", title: "Lead submitted", lines });
      setForm(EMPTY);
      setQualified(false);
    } catch (err) {
      const e = parseApiError(err);
      const lines = [e.message];
      if (e.missing?.length) lines.push(`Missing: ${e.missing.join(", ")}`);
      if (e.allowed?.length) lines.push(`Allowed: ${e.allowed.join(", ")}`);
      setResult({ kind: "err", title: "Submission rejected", lines });
    } finally {
      setBusy(false);
    }
  }, [buildPayload, clientValidate, crmApi]);

  return (
    <div className={styles.root}>
      <Text size={600} weight="semibold" block>New Lead</Text>
      <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalM }}>
        Create a new opportunity in the CRM — deduped and auto-assigned by state.
      </Text>

      <Text className={styles.section} block>Agency &amp; contact</Text>
      <div className={styles.grid}>
        <Field className={styles.full} label="Agency name" required>
          <Input value={form.agency_name} onChange={(_, d) => set("agency_name", d.value)} />
        </Field>
        <Field label="Primary contact name" required>
          <Input value={form.primary_contact_name} onChange={(_, d) => set("primary_contact_name", d.value)} />
        </Field>
        <Field label="Rank / title">
          <Select value={form.rank_title} onChange={(_, d) => set("rank_title", d.value)}>
            <option value="">Select…</option>
            {RANK_TITLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="Email" hint="Email or phone required">
          <Input type="email" value={form.email} onChange={(_, d) => set("email", d.value)} />
        </Field>
        <Field label="Phone" hint="Email or phone required">
          <Input value={form.phone} onChange={(_, d) => set("phone", d.value)} />
        </Field>
        <Field label="Street address">
          <Input value={form.address1} onChange={(_, d) => set("address1", d.value)} />
        </Field>
        <Field label="City">
          <Input value={form.city} onChange={(_, d) => set("city", d.value)} />
        </Field>
        <Field label="State" required hint="Drives territory / LERT assignment">
          <Select value={form.state} onChange={(_, d) => set("state", d.value)}>
            <option value="">Select…</option>
            {US_STATES.map(([abbr, name]) => <option key={abbr} value={abbr}>{name}</option>)}
          </Select>
        </Field>
        <Field label="ZIP / postal code">
          <Input value={form.postal_code} onChange={(_, d) => set("postal_code", d.value)} />
        </Field>
      </div>

      <Text className={styles.section} block>Opportunity details</Text>
      <div className={styles.grid}>
        <Field label="Lead source">
          <Select value={form.source} onChange={(_, d) => set("source", d.value)}>
            <option value="">Select…</option>
            {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Number of BWC (officers)">
          <Input type="number" value={form.officer_count} onChange={(_, d) => set("officer_count", d.value)} />
        </Field>
        <Field label="Number of in-car (vehicles)">
          <Input type="number" value={form.car_count} onChange={(_, d) => set("car_count", d.value)} />
        </Field>
        <Field label="Current camera vendor">
          <Select value={form.current_camera_vendor} onChange={(_, d) => set("current_camera_vendor", d.value)}>
            <option value="">Select…</option>
            {CAMERA_VENDOR_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Timeframe to buy">
          <Input value={form.timeframe_to_buy} onChange={(_, d) => set("timeframe_to_buy", d.value)} placeholder="e.g. 0–3 months" />
        </Field>
        <Field label="Expected close date">
          <Input type="date" value={form.expected_close_date} onChange={(_, d) => set("expected_close_date", d.value)} />
        </Field>
        <Field label="How did they hear about us?">
          <Input value={form.how_did_you_hear} onChange={(_, d) => set("how_did_you_hear", d.value)} />
        </Field>
        <Field className={styles.full} label="Notes / description">
          <Textarea value={form.description} onChange={(_, d) => set("description", d.value)} resize="vertical" />
        </Field>
      </div>

      <div className={styles.toggleRow}>
        <Switch checked={qualified} onChange={(_, d) => setQualified(!!d.checked)} />
        <div>
          <Text weight="semibold" block>{qualified ? "Qualified lead" : "Unqualified lead"}</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {qualified ? "Lands at Step 2 — Lead Qualified." : "Lands at Step 1 — Lead Identified."}
          </Text>
        </div>
      </div>

      <div className={styles.actions}>
        <Button appearance="primary" disabled={busy} onClick={submit}>
          {busy ? <Spinner size="tiny" /> : "Create lead"}
        </Button>
      </div>

      {result && (
        <Card className={`${styles.result} ${styles[result.kind]}`}>
          <Text weight="semibold" block>{result.title}</Text>
          {result.lines.map((l, i) => <Text key={i} block size={300}>{l}</Text>)}
        </Card>
      )}
    </div>
  );
};

export default NewLead;
